import type { FishStockLog, MortalityLog } from '../types/production'
import type { BiomassDataRow } from './productionUnitTable'
import type { LegacyDuplicateGroup } from './productionLegacyDuplicateGroups'
import type { SamplingAggregate, SamplingEvent } from './samplingEventMath'
import type { WeightSampleLogCreatePayload } from './weightSampleLogApi'
import { computeMeanWeightG, recordSamplingEvent } from './samplingEventMath'
import { findAllLinkedMortalityLogsForRow } from '../screens/biomass/mortalityLogBridge'
import { buildBiomassSamplingWeightMetadata } from '../screens/biomass/weightSampleLogBridge'

// v2.1 finding #36: merge planner for legacy duplicate `(lot, day)` rows.
// The planner is intentionally pure so the confirmation modal can preview the
// outcome before any side effects fire. The applier in
// `applyLegacyDuplicateMergePlan` runs upserts/deletes/log-rebinds against the
// canonical APIs once the operator confirms.

export type LegacyDuplicateLogRebind = {
  logId: string
  nextMetadata: Record<string, unknown>
}

/**
 * v2.2 finding #2 (2026-07-13): rebind descriptor for `FishStockLog` events
 * bound to an absorbed row. Unlike `MortalityLog`, `FishStockLog` carries a
 * top-level `productionRowId` field in addition to `metadata.productionRowId`,
 * so both fields must be rewritten when the underlying row collapses into the
 * kept row. The applier calls `fishStockLogApi.update(logId, { productionRowId,
 * metadata })` for each entry.
 */
export type LegacyDuplicateFishStockLogRebind = {
  logId: string
  nextProductionRowId: string
  nextMetadata: Record<string, unknown>
}

export type LegacyDuplicateMergePlan = {
  group: LegacyDuplicateGroup
  /** The row that receives merged data (kept after the migration). */
  keptRow: BiomassDataRow
  /** The kept row with merged fields applied (`weight` = mean, `fishStock` =
   * latest non-blank, `dailyMortality` = sum across absorbed rows so legacy
   * CSV-imported mortality is preserved post-merge, notes concatenated). */
  mergedRow: BiomassDataRow
  /** Rows that will be deleted via `deleteProductionUnitTableRow`. */
  absorbedRows: BiomassDataRow[]
  /** MortalityLogs whose metadata `productionRowId` must be rebound from an
   * absorbed row to the kept row before the absorbed row is deleted. */
  logRebinds: LegacyDuplicateLogRebind[]
  /** v2.2 finding #2: FishStockLogs whose top-level `productionRowId` (and
   * paired `metadata.productionRowId`) must be rebound from an absorbed row to
   * the kept row. Mirrors `logRebinds` for the Tier A fish-stock stream so the
   * per-event provenance survives fusion. Pre-fix path deleted these events
   * outright, leaving the merged row's fish-stock overlay blank. */
  fishStockLogRebinds: LegacyDuplicateFishStockLogRebind[]
  /** Sampling history to persist on the kept row so the SamplingEventsPopover
   * surfaces evidence for the new mean. */
  nextSamplingAggregate: SamplingAggregate
}

const parseTimestamp = (raw: string | undefined): number => {
  if (!raw) return Number.POSITIVE_INFINITY
  const t = new Date(raw).getTime()
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY
}

const pickKeptRow = (rows: BiomassDataRow[]): BiomassDataRow => {
  // Prefer the earliest creation time when available; otherwise fall back to
  // earliest `date`, otherwise the first row in insertion order. We pick the
  // earliest because operator-visible state should preserve the row id the
  // user saw first (cancels orphan/ghost ids that may have been added later).
  const withTimes = rows.map((row, index) => ({
    row,
    index,
    createdAt: parseTimestamp((row as any).createdAt),
    date: parseTimestamp(row.date)
  }))
  withTimes.sort((a, b) => {
    if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt
    if (a.date !== b.date) return a.date - b.date
    return a.index - b.index
  })
  return withTimes[0].row
}

const seedSamplingEvents = (rows: BiomassDataRow[]): SamplingEvent[] => {
  const events: SamplingEvent[] = []
  for (const row of rows) {
    const raw = String(row.weight ?? '').trim()
    if (!raw) continue
    const numeric = Number(raw)
    if (!Number.isFinite(numeric) || numeric <= 0) continue
    events.push({ recordedAt: String(row.date ?? ''), weightG: numeric })
  }
  // Stable sort by recordedAt then numeric weight so deterministic mean.
  events.sort((a, b) => {
    const aTime = parseTimestamp(a.recordedAt)
    const bTime = parseTimestamp(b.recordedAt)
    if (aTime !== bTime) return aTime - bTime
    return a.weightG - b.weightG
  })
  return events
}

const pickLatestFishStock = (rows: BiomassDataRow[]): string => {
  // Latest non-blank fishStock wins; ties broken by recordedAt descending.
  const candidates = rows
    .map((row, index) => ({
      row,
      index,
      time: parseTimestamp(row.date),
      value: String(row.fishStock ?? '').trim()
    }))
    .filter((c) => c.value !== '')
  if (candidates.length === 0) return ''
  candidates.sort((a, b) => {
    if (a.time !== b.time) return b.time - a.time
    return b.index - a.index
  })
  return candidates[0].value
}

const concatenateNotes = (rows: BiomassDataRow[], field: string): string => {
  const parts: string[] = []
  const seen = new Set<string>()
  for (const row of rows) {
    const raw = String(row[field] ?? '').trim()
    if (!raw || seen.has(raw)) continue
    parts.push(raw)
    seen.add(raw)
  }
  return parts.join('; ')
}

/**
 * v2.1 finding #48: Sum the stored `dailyMortality` across every row in the
 * group so the kept row preserves CSV-imported mortality after fusion.
 *
 * The MORT-12 overlay still REPLACES (not adds) the displayed value with the
 * event-derived sum whenever any `MortalityLog` exists for `(day, lot)`, so
 * this stored sum is only visible when no events exist — which is exactly the
 * legacy CSV path where the stored cell IS the canonical source of truth.
 *
 * Blank or non-numeric cells contribute 0. When every cell is blank the
 * kept row stays blank so we don't introduce a synthetic "0" where the user
 * never recorded one.
 */
const sumStoredDailyMortality = (rows: BiomassDataRow[]): string => {
  let total = 0
  let sawAnyNumeric = false
  for (const row of rows) {
    const raw = String(row.dailyMortality ?? '').trim()
    if (!raw) continue
    const numeric = Number(raw)
    if (!Number.isFinite(numeric)) continue
    total += numeric
    sawAnyNumeric = true
  }
  if (!sawAnyNumeric) return ''
  return String(total)
}

/**
 * Shared metadata-rebind implementation: preserves every existing metadata
 * field and rewrites `productionRowId` to the new kept row id. Returns a new
 * object so callers can safely mutate the result without touching the log.
 */
const rebindMetadataProductionRowId = (
  metadata: Record<string, unknown> | undefined,
  keptRowId: string
): Record<string, unknown> => {
  const base = metadata && typeof metadata === 'object' && !Array.isArray(metadata)
    ? { ...metadata }
    : {}
  return { ...base, productionRowId: keptRowId }
}

/**
 * Build a metadata object that rebinds `productionRowId` to the new kept row
 * while preserving the original `source`, `lotId`, and `stage` shape produced
 * by `buildBiomassSamplingMortalityMetadata`. Any unknown fields are passed
 * through untouched so we don't drop API-introduced metadata.
 */
export const buildRebindMetadata = (
  log: MortalityLog,
  keptRowId: string
): Record<string, unknown> => rebindMetadataProductionRowId(log.metadata, keptRowId)

/**
 * v2.2 finding #2: parallel to `buildRebindMetadata` for `FishStockLog`.
 * Preserves all existing metadata fields (e.g. `source: 'biomassFishStock'`,
 * `lotId`, `stage`) while rewriting `productionRowId` to the kept row id.
 */
export const buildFishStockLogRebindMetadata = (
  log: FishStockLog,
  keptRowId: string
): Record<string, unknown> => rebindMetadataProductionRowId(log.metadata, keptRowId)

/**
 * v2.2 finding #2 helper: extract the `productionRowId` a `FishStockLog` is
 * currently bound to, checking the top-level column first and falling back to
 * `metadata.productionRowId`. Returns an empty string for day-scoped logs.
 */
const readFishStockLogBoundRowId = (log: FishStockLog): string => {
  const topLevel = typeof log.productionRowId === 'string' ? log.productionRowId.trim() : ''
  if (topLevel) return topLevel
  const metadata = log.metadata && typeof log.metadata === 'object' && !Array.isArray(log.metadata)
    ? log.metadata
    : undefined
  const metadataRowId = metadata && typeof metadata.productionRowId === 'string'
    ? metadata.productionRowId.trim()
    : ''
  return metadataRowId
}

/**
 * v2.2 finding #2: enumerate `FishStockLog` rebinds needed to collapse
 * absorbed-row-bound events onto the kept row. Encapsulated so
 * `planLegacyDuplicateMerge`'s cognitive complexity stays contained.
 */
const buildFishStockLogRebindsForMerge = (
  fishStockLogs: FishStockLog[],
  absorbedRowIds: Set<string>,
  keptRowId: string
): LegacyDuplicateFishStockLogRebind[] => {
  const rebinds: LegacyDuplicateFishStockLogRebind[] = []
  for (const log of fishStockLogs) {
    const boundRowId = readFishStockLogBoundRowId(log)
    if (!boundRowId) continue
    if (boundRowId === keptRowId) continue
    if (!absorbedRowIds.has(boundRowId)) continue
    rebinds.push({
      logId: log.id,
      nextProductionRowId: keptRowId,
      nextMetadata: buildFishStockLogRebindMetadata(log, keptRowId)
    })
  }
  return rebinds
}

export const planLegacyDuplicateMerge = (
  group: LegacyDuplicateGroup,
  mortalityLogs: MortalityLog[],
  fishStockLogs: FishStockLog[] = []
): LegacyDuplicateMergePlan => {
  const keptRow = pickKeptRow(group.rows)
  const absorbedRows = group.rows.filter((row) => row.id !== keptRow.id)

  // v2.1 finding #60 (WSL-08): the localStorage sidecar retired, so the
  // planner no longer carries a prior aggregate forward across calls. Every
  // migration seeds fresh from the rows' stored `weight` cells — which is
  // exactly the data the operator saw when they clicked "Fuse duplicates".
  // Any per-event history that existed before WSL-04 was already discarded
  // per the WSL cluster's rollout note; the mean the planner produces here is
  // the correct summary of what the group's ProductionRecord rows contain.
  let workingAggregate: Record<string, SamplingAggregate> = {
    [keptRow.id]: { events: seedSamplingEvents([keptRow]) }
  }

  const absorbedEvents = seedSamplingEvents(absorbedRows)
  for (const event of absorbedEvents) {
    const result = recordSamplingEvent(workingAggregate, keptRow.id, event)
    workingAggregate = result.nextByRowId
  }

  const nextSamplingAggregate = workingAggregate[keptRow.id] ?? { events: [] }
  const mean = computeMeanWeightG(nextSamplingAggregate.events)
  const mergedWeight = mean === null ? String(keptRow.weight ?? '') : String(Number(mean.toFixed(3)))

  const mergedFishStock = pickLatestFishStock(group.rows)
  const mergedNotes = concatenateNotes(group.rows, 'notes') || concatenateNotes(group.rows, 'observations')
  const mergedDailyMortality = sumStoredDailyMortality(group.rows)

  const mergedRow: BiomassDataRow = {
    ...keptRow,
    weight: mergedWeight,
    fishStock: mergedFishStock,
    // v2.1 finding #48: preserve the stored mortality sum across absorbed rows
    // so CSV-imported legacy data isn't lost. The MORT-12 overlay still
    // overrides this at render time whenever any `MortalityLog` exists for
    // `(day, lot)`, so the events path keeps its precedence intact.
    dailyMortality: mergedDailyMortality
  }
  if (mergedNotes) {
    ;(mergedRow as Record<string, string | undefined>).notes = mergedNotes
  }

  // Rebind every linked MortalityLog whose metadata points at one of the
  // absorbed rows. Logs pointing at the kept row (or with no metadata) stay
  // matched via the (date, lot) fallback.
  const absorbedRowIds = new Set(absorbedRows.map((r) => r.id))
  const linkedLogs = findAllLinkedMortalityLogsForRow(keptRow, mortalityLogs)
  const logRebinds: LegacyDuplicateLogRebind[] = []
  for (const log of linkedLogs) {
    const metadata = log.metadata
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) continue
    const currentRowId = typeof metadata.productionRowId === 'string' ? metadata.productionRowId.trim() : ''
    if (!currentRowId) continue
    if (currentRowId === keptRow.id) continue
    if (!absorbedRowIds.has(currentRowId)) continue
    logRebinds.push({
      logId: log.id,
      nextMetadata: buildRebindMetadata(log, keptRow.id)
    })
  }

  // v2.2 finding #2: mirror the MortalityLog rebind for `FishStockLog` so
  // absorbed-row-bound fish-stock events collapse into the kept row instead
  // of being deleted (pre-fix behaviour). Scans by top-level `productionRowId`
  // OR `metadata.productionRowId` — either reference to an absorbed row id
  // qualifies for rebind. Day-scoped logs (no productionRowId anywhere) are
  // intentionally excluded: the overlay already matches them by (date, lot)
  // and the kept row shares that key, so they attach naturally post-fusion.
  const fishStockLogRebinds = buildFishStockLogRebindsForMerge(
    fishStockLogs,
    absorbedRowIds,
    keptRow.id
  )

  return {
    group,
    keptRow,
    mergedRow,
    absorbedRows,
    logRebinds,
    fishStockLogRebinds,
    nextSamplingAggregate
  }
}

/**
 * A1.1 Fix A (follow-up to PR #69): build the `weightSampleLogApi.create`
 * payload for one synthesized sampling event of a legacy-duplicate merge.
 *
 * `lotId` MUST land at the top level of the payload, not only inside
 * `metadata`: `lotMatches` (packages/api/src/utils/currentBiomassPerLot.ts)
 * and `LotStoryService` read the `lotId` column. A metadata-only lotId drops
 * the merged row's weight history out of the lot story the moment the lot is
 * transferred or moved between production units, because the lot-label
 * fallback no longer resolves. Both the column and `metadata.lotId` derive
 * from the same local here so they can never diverge.
 *
 * An absent lotId yields `undefined` (persisted as NULL) rather than an empty
 * string, while `metadata.lotId` keeps its historical `''` shape so already
 * written logs and newly written ones compare equal.
 */
export const buildLegacyDuplicateMergeWeightSamplePayload = (args: {
  farmId: string
  productionUnitId: string
  mergedRow: BiomassDataRow
  event: SamplingEvent
}): WeightSampleLogCreatePayload => {
  const { farmId, productionUnitId, mergedRow, event } = args
  const lotId = String(mergedRow.lotId ?? '').trim()
  const stage: 'Active' | 'Inactive' =
    String(mergedRow.stage ?? '') === 'Inactive' ? 'Inactive' : 'Active'

  return {
    farmId,
    productionUnitId,
    lot: String(mergedRow.lot ?? '') || `Lot ${mergedRow.id}`,
    lotId: lotId || undefined,
    productionRowId: mergedRow.id,
    recordedAt: event.recordedAt,
    weightGrams: String(event.weightG),
    metadata: buildBiomassSamplingWeightMetadata({
      productionRowId: mergedRow.id,
      lotId,
      stage
    })
  }
}

export const planAllLegacyDuplicateMerges = (
  groups: LegacyDuplicateGroup[],
  mortalityLogs: MortalityLog[],
  fishStockLogs: FishStockLog[] = []
): LegacyDuplicateMergePlan[] => {
  // v2.1 finding #60 (WSL-08): plans are independent now that no aggregate is
  // carried forward — disjoint groups produce disjoint plans directly from
  // each group's own row weights.
  const plans: LegacyDuplicateMergePlan[] = []
  for (const group of groups) {
    plans.push(planLegacyDuplicateMerge(group, mortalityLogs, fishStockLogs))
  }
  return plans
}
